import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Navigate, Route, Routes } from "react-router";
import { useTranslation } from "react-i18next";
import { signOut } from "aws-amplify/auth";
import { VelavuAlert, VelavuAPI, VelavuUser } from "velavu-js-api";
import { useVelavuModal } from "../../elements/velavu-modal";
import { VelavuTitledModal } from "../../elements/velavu-titled-modal";
import { useCancellableEffect } from "../../helper/hook-helper";
import { useInactivityTimeout } from "../../helper/auth-helper";
import SimpleObservable from "../../helper/simple-observable";
import * as storageHelper from "../../helper/storage-helper";
import {
	useHasPermission,
	useSignInContext,
} from "../sign-in-context/sign-in-context";
import { RequirePermission } from "../sign-in-context/require-permission";
import Alerts from "../alerts/alerts";
import Analytics from "../analytics/analytics";
import Assets from "../assets/assets";
import AssetNew from "../assets/create/asset-new";
import AssetNewConfirm from "../assets/create/asset-new-confirm";
import AssetNewTag from "../assets/create/asset-new-tag";
import Dashboard from "../dashboard/dashboard";
import Page from "../page/page";
import SettingsModal, { SettingsTab } from "../settings/settings-modal";
import Sidebar from "../sidebar/sidebar";
import Devices from "../devices/devices";
import * as styles from "./content.module.scss";
import OnboardingDialog from "../onboarding/onboarding-dialog";
import { useContentRefresh } from "../../elements/refresh-context";
import Rules from "../rules/rules";
import RulesEditorPath from "../rules/editor/rules-editor-path";
import Inventory from "../inventory/list/inventory";
import InventoryDetailPath from "../inventory/detail/inventory-detail-path";
import { useNewAlertListener } from "velavu-react";
import InventoryDetail from "../inventory/detail/inventory-detail";

export default function Content() {
	const { t } = useTranslation();
	const signInDetails = useSignInContext();

	const canDeleteAlerts = useHasPermission("alerts:delete");

	useInactivityTimeout(
		useCallback(() => {
			signOut().catch(console.log);
		}, []),
	);

	const [isExpanded, setExpanded] = useState(true);
	const expandedObservable = useMemo(() => new SimpleObservable<void>(), []);
	const updateExpanded = useCallback(
		(updater: (isExpanded: boolean) => boolean) => {
			setExpanded(updater);
			expandedObservable.emit(undefined);
		},
		[setExpanded, expandedObservable],
	);

	const [alerts, setAlerts] = useState<undefined | VelavuAlert[]>(undefined);
	const [showAlerts, setShowAlerts] = useState(false);
	const hideAlerts = useCallback(() => {
		setShowAlerts(false);
	}, [setShowAlerts]);
	const [readAlertIDs, setReadAlertIDs] = useState<string[]>([]);
	const alertCount = useMemo(() => {
		if (!alerts || !readAlertIDs) return 0;
		else return alerts.length - readAlertIDs.length;
	}, [alerts, readAlertIDs]);

	useCancellableEffect(
		(addPromise) => {
			//Waiting for the user to be signed in and have their preferences loaded
			if (signInDetails.velavuUser === undefined) return;

			//Fetching alerts
			addPromise(
				VelavuAPI.alerts
					.getAllAlerts({
						eventCategories:
							signInDetails.preferences.alertsEventCategoryFilter,
					})
					.then((result) => result.data),
			)
				.then((alerts: VelavuAlert[]) => {
					setAlerts(alerts);
					setReadAlertIDs(storageHelper.getReadAlertIDs(alerts));
				})
				.catch(console.log);
		},
		[
			signInDetails.velavuUser,
			signInDetails.preferences.alertsEventCategoryFilter,
		],
	);

	//Subscribe to new alerts
	useNewAlertListener(
		useCallback(
			(alert) => {
				//Add the alert
				if (
					signInDetails.preferences.alertsEventCategoryFilter.includes(
						alert.event_category,
					)
				) {
					setAlerts((alerts) =>
						alerts ? [alert, ...alerts] : undefined,
					);
				}
			},
			[setAlerts, signInDetails.preferences.alertsEventCategoryFilter],
		),
	);

	const dismissAlert = useCallback(
		(alert: VelavuAlert) => {
			//Updating the state in memory
			setReadAlertIDs((readAlertIDs) => readAlertIDs.concat(alert.id));

			//Updating the state on disk
			storageHelper.markAlertIDRead(alert.id);
		},
		[setReadAlertIDs],
	);

	const deleteAlert = useCallback(
		(alert: VelavuAlert) => {
			if (!canDeleteAlerts) return;

			//Updating the state in memory
			setReadAlertIDs((array) =>
				array.filter((alertID) => alertID !== alert.id),
			);
			setAlerts((array) =>
				array?.filter((allAlerts) => allAlerts.id !== alert.id),
			);

			//Updating the state on disk
			storageHelper.markAlertIDDeleted(alert.id);

			//Updating the state on the backend
			VelavuAPI.alerts.deleteAlert(alert.id).catch((error) => {
				console.log(error);
				window.alert(t("content.alerts.delete_failed"));
			});
		},
		[canDeleteAlerts, setReadAlertIDs, setAlerts, t],
	);

	const dismissAllAlerts = useCallback(() => {
		if (!alerts) return;

		//Updating the state in memory
		setReadAlertIDs(alerts.map((alert) => alert.id) ?? []);

		//Updating the state on disk
		storageHelper.setReadAlertIDs(alerts);
	}, [alerts, setReadAlertIDs]);

	const deleteAllAlerts = useCallback(() => {
		if (!alerts || !canDeleteAlerts) return;

		//Updating the state in memory
		setAlerts([]);
		setReadAlertIDs([]);

		//Updating the state on disk
		storageHelper.setReadAlertIDs([]);

		//Updating the state on the backend
		VelavuAPI.alerts.deleteAllAlerts().catch((error) => {
			console.log(error);
			window.alert(t("content.alerts.delete_all_failed"));
		});
	}, [alerts, canDeleteAlerts, setAlerts, setReadAlertIDs, t]);

	const { pushModal } = useVelavuModal();

	const openSettings = useCallback(
		(initialTabKey?: SettingsTab) => {
			pushModal(
				(resolve, onTryResolve) => (
					<VelavuTitledModal
						title={t("sidebar.user_menu.settings")}
						onClose={resolve}
					>
						<SettingsModal
							onTryResolve={onTryResolve}
							initialTabKey={initialTabKey}
						/>
					</VelavuTitledModal>
				),
				{ pinToTop: true },
			);
		},
		[pushModal, t],
	);

	const onboardingOpened = useRef(false);
	const updateVelavuUser = signInDetails.updateVelavuUser;
	const openOnboarding = useCallback(async () => {
		//Make sure we only open a single dialog
		if (onboardingOpened.current) return;
		onboardingOpened.current = true;

		//Check if this is the first user
		const users: VelavuUser[] = await VelavuAPI.users.getAllUsers();
		const isFirstUser = users.length === 1;

		//Show the onboarding dialog
		await pushModal<boolean>((resolve, onTryResolve) => {
			//Fail resolutions by default
			onTryResolve.subscribe((data) => {
				if (!data.resolve) {
					data.cancel();
				}
			});

			return (
				<OnboardingDialog
					resolve={() => resolve(true)}
					isFirstUser={isFirstUser}
				/>
			);
		});

		//Record that the user has seen the welcome screen
		updateVelavuUser({
			settings: {
				showWelcomeScreen: false,
			},
		});
	}, [updateVelavuUser, pushModal]);
	useEffect(() => {
		//Show the welcome screen to the user the first time
		if (
			signInDetails.velavuUser !== undefined &&
			signInDetails.preferences.showWelcomeScreen
		) {
			openOnboarding();
		}
	}, [
		signInDetails.velavuUser,
		signInDetails.preferences.showWelcomeScreen,
		openOnboarding,
	]);

	const contentRefresh = useContentRefresh();

	return (
		<div className={styles.container}>
			<Sidebar
				isExpanded={isExpanded}
				setExpanded={updateExpanded}
				isRefreshing={contentRefresh.isRefreshing}
				showAlerts={showAlerts}
				setShowAlerts={setShowAlerts}
				alertCount={alertCount}
				onOpenSettings={openSettings}
				onRefreshContent={contentRefresh.refresh}
			/>

			<Page>
				<Alerts
					show={showAlerts}
					onClose={hideAlerts}
					alerts={alerts}
					alertCount={alertCount}
					readAlertIDs={readAlertIDs}
					onDismiss={dismissAlert}
					onDelete={canDeleteAlerts ? deleteAlert : undefined}
					onDismissAll={dismissAllAlerts}
					onDeleteAll={canDeleteAlerts ? deleteAllAlerts : undefined}
					onOpenAlertsSettings={() =>
						openSettings(SettingsTab.Alerts)
					}
				/>
				<Routes>
					<Route path="/" element={<Navigate to="/map" replace />} />
					<Route
						path="map/device?/:deviceID?"
						element={
							<Dashboard
								resizeObservable={expandedObservable}
								onDeepLinkSelectDevice={hideAlerts}
							/>
						}
					/>
					<Route path="assets" element={<Assets />} />
					<Route
						path="assets/create"
						element={
							<RequirePermission permissions={["assets:write"]}>
								<AssetNew />
							</RequirePermission>
						}
					/>
					<Route
						path="assets/create/tag"
						element={
							<RequirePermission permissions={["assets:write"]}>
								<AssetNewTag />
							</RequirePermission>
						}
					/>
					<Route
						path="assets/create/confirm"
						element={
							<RequirePermission permissions={["assets:write"]}>
								<AssetNewConfirm />
							</RequirePermission>
						}
					/>
					<Route path="devices" element={<Devices />} />
					<Route path="analytics" element={<Analytics />} />
					<Route
						path="rules"
						element={
							<RequirePermission permissions={["rules:read"]}>
								<Rules />
							</RequirePermission>
						}
					/>
					<Route
						path="rules/:ruleID"
						element={
							<RequirePermission permissions={["rules:read"]}>
								<RulesEditorPath />
							</RequirePermission>
						}
					/>
					{process.env.FEATURE_FLAGS.enableInventory && (
						<>
							<Route
								path="inventory"
								element={
									<RequirePermission
										permissions={["inventories:read"]}
									>
										<Inventory />
									</RequirePermission>
								}
							/>
							<Route
								path="inventory/:inventoryZoneID"
								element={
									<RequirePermission
										permissions={["inventories:read"]}
									>
										<InventoryDetailPath />
									</RequirePermission>
								}
							/>
						</>
					)}
				</Routes>
			</Page>
		</div>
	);
}
